import React, { Component } from 'react';
import axios from "axios";
import moment from 'moment';
import {
    Alert,
    Box,
    Button,
    Divider,
    Grid,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import Api from '../../customlib/Api';
import DateRangePickerRich from '../../customlib/DateRangePickerRich';

function formatDuration(seconds) {
    if (seconds === null || seconds === undefined) return '-';
    const h = Math.floor(seconds / 3600);
    const m = Math.round((seconds % 3600) / 60);
    return h > 0 ? `${h}j ${m}m` : `${m}m`;
}

class CsAgentsPerformancesReport extends Component {
    state = {
        startDate: moment().tz("asia/jakarta").subtract(6, "days"),
        endDate: moment().tz("asia/jakarta"),
        rows: [],
        loading: false,
        error: "",
    }

    componentDidMount() {
        this.load();
    }

    load = () => {
        this.setState({ loading: true, error: "" }, () => {
            const params = {
                from: this.state.startDate.clone().startOf("day").utc().format("YYYY-MM-DD HH:mm:ss"),
                to: this.state.endDate.clone().endOf("day").utc().format("YYYY-MM-DD HH:mm:ss"),
            };

            axios
                .get(Api.csReportAgentPerformance(params), Api.getRequestHeader())
                .then((response) => {
                    const data = response.data;
                    if (data.success === true) {
                        const rows = (data.data?.rows || []).slice().sort((a, b) => b.total_handled - a.total_handled);
                        this.setState({ loading: false, rows });
                    } else {
                        this.setState({
                            loading: false,
                            rows: [],
                            error: data.message || "Gagal memuat data performa agent.",
                        });
                    }
                })
                .catch((error) => {
                    this.setState({
                        loading: false,
                        rows: [],
                        error: error.response?.data?.message || error.message || "Terjadi kesalahan saat memuat data.",
                    });
                    console.error("There was an error!", error);
                });
        });
    }

    handleDateChanged = (dateRange) => {
        if (dateRange && dateRange[0] && dateRange[1]) {
            this.setState({ startDate: dateRange[0], endDate: dateRange[1] }, this.load);
        }
    }

    render() {
        const { rows, loading, error } = this.state;

        const totalHandled = rows.reduce((sum, row) => sum + (row.total_handled || 0), 0);
        const totalClosed = rows.reduce((sum, row) => sum + (row.total_closed || 0), 0);

        return (
            <Box width={"100%"}>
                {error && (
                    <Alert
                        severity="error"
                        onClose={() => this.setState({ error: "" })}
                        sx={{ mb: 2 }}
                    >
                        {error}
                    </Alert>
                )}

                {/* === Baris 1 : Filter === */}
                <Grid container spacing={1}>
                    <Grid item xs={7}></Grid>
                    <Grid item xs={1} textAlign={"left"}>
                        <Box sx={{ mt: "10px" }}>Tanggal : </Box>
                    </Grid>
                    <Grid item xs={3}>
                        <Box sx={{ mt: "5px" }}>
                            <DateRangePickerRich title={"Tanggal Tiket"} startDate={this.state.startDate} endDate={this.state.endDate} onDateChanged={this.handleDateChanged} />
                        </Box>
                    </Grid>
                    <Grid item xs={1} textAlign={"center"}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<RefreshIcon fontSize="small" />}
                            onClick={this.load}
                            disabled={loading}
                            sx={{ mt: "3px", textTransform: "none" }}
                        >
                            Refresh
                        </Button>
                    </Grid>
                </Grid>

                {
                    loading ? <LinearProgress sx={{ mt: "10px", mb: "10px" }} /> : <Divider sx={{ mt: "10px", mb: "10px" }} />
                }

                {/* === Baris 2 : Summary === */}
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    <Grid item>
                        <Box sx={{ minWidth: 130, borderRadius: "10px", bgcolor: "#e3f2fd", color: "#1565c0", px: 2, py: 1.5, textAlign: "center" }}>
                            <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>{rows.length}</Typography>
                            <Typography variant="caption" fontWeight={600}>Total Agent</Typography>
                        </Box>
                    </Grid>
                    <Grid item>
                        <Box sx={{ minWidth: 130, borderRadius: "10px", bgcolor: "#e8f5e9", color: "#2e7d32", px: 2, py: 1.5, textAlign: "center" }}>
                            <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>{totalHandled}</Typography>
                            <Typography variant="caption" fontWeight={600}>Total Tiket Ditangani</Typography>
                        </Box>
                    </Grid>
                    <Grid item>
                        <Box sx={{ minWidth: 130, borderRadius: "10px", bgcolor: "#fff3e0", color: "#ef6c00", px: 2, py: 1.5, textAlign: "center" }}>
                            <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>{totalClosed}</Typography>
                            <Typography variant="caption" fontWeight={600}>Total Tiket Selesai</Typography>
                        </Box>
                    </Grid>
                </Grid>

                {/* === Baris 3 : Table === */}
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                                <TableCell><strong>Agent</strong></TableCell>
                                <TableCell align="right"><strong>Total Ditangani</strong></TableCell>
                                <TableCell align="right"><strong>Total Selesai</strong></TableCell>
                                <TableCell align="right"><strong>Tingkat Penyelesaian</strong></TableCell>
                                <TableCell align="right"><strong>Rata-rata Respon Pertama</strong></TableCell>
                                <TableCell align="right"><strong>Rata-rata Tangani s/d Selesai</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                            Tidak ada data performa agent.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {rows.map((row) => {
                                const completionRate = row.total_handled > 0
                                    ? Math.round((row.total_closed / row.total_handled) * 100)
                                    : 0;
                                return (
                                    <TableRow key={row.handled_by} hover>
                                        <TableCell>{row.handled_by_name || `User #${row.handled_by}`}</TableCell>
                                        <TableCell align="right">{row.total_handled}</TableCell>
                                        <TableCell align="right">{row.total_closed}</TableCell>
                                        <TableCell align="right">{completionRate}%</TableCell>
                                        <TableCell align="right">{formatDuration(row.avg_response_sec)}</TableCell>
                                        <TableCell align="right">{formatDuration(row.avg_handle_to_complete_sec)}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    }
}

export default CsAgentsPerformancesReport;
